import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import VoDemoContainer from "@/page/VoDemoContainer";
import { RuntimeProvider } from "@/features/tutor/context/RuntimeContext.tsx";
import { CooldownProvider } from "@/features/tutor/context/CooldownContext";
import SlideEditor from "@/page/SlideEditor.jsx";
import HtmlViewer from "@/page/HtmlViewer.jsx";
import SlideViewerPage from "@/page/SlideViewerPage";
import Login from "@/page/Login.tsx";
import SignUp from "@/page/SignUp.tsx";
import ResetPassword from "@/page/ResetPassword.tsx";
import SupportLogin from "@/page/SupportLogin.tsx";
import Content from "@/components/Content";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleBasedView from "@/components/RoleBasedView";
import NotificationStack from "@/components/notifications/NotificationStack";
import StudentLessonsView from "@/features/student/components/StudentLessonsView.tsx";
import TeacherLessonsView from "@/features/teacher/components/TeacherLessonsView.tsx";
import StudentClassroomsView from "@/features/student/components/StudentClassroomsView.tsx";
import TeacherClassroomsView from "@/features/teacher/components/TeacherClassroomsView.tsx";
import LessonSequencingPage from "@/features/teacher/components/LessonSequencingPage.tsx";
import CurriculumManagementPage from "@/features/teacher/components/CurriculumManagementPage.tsx";
import DashboardView from "@/features/analytics/DashboardView.tsx";
import TeacherSpacePage from "@/features/spaces/pages/TeacherSpacePage";
import StudentSpacePage from "@/features/spaces/pages/StudentSpacePage";
import CreateSpacePage from "@/features/spaces/pages/CreateSpacePage";
import SpacesListPage from "@/features/spaces/pages/SpacesListPage";
import TeacherRecapPage from "@/features/spaces/pages/TeacherRecapPage";
import StudentRecapPage from "@/features/spaces/pages/StudentRecapPage";
import TeacherViewWorkPage from "@/features/spaces/pages/TeacherViewWorkPage";
import { TutorMode } from "@/constants/mode";
import {
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";
import { useLessonSequencingEnabled } from "@/hooks/useRuntimeFlags";
import { FormProvider } from "@/features/tutor/context/FormContext";
import { RemountOnLessonChange } from "@/routes/RemountOnLessonChange";

const AppRoutes = () => {
  const lessonSequencingEnabled = useLessonSequencingEnabled();
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" />} />
      <Route
        path="/signup"
        element={
          <>
            <NotificationStack
              stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
            />
            <SignUp />
          </>
        }
      />
      <Route
        path="/login"
        element={
          <>
            <NotificationStack
              stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
            />
            <Login />
          </>
        }
      />
      {/* Support-login hand-off landing. Public: the visitor is not signed
          in as anyone until the grant is redeemed. */}
      <Route path="/support-login" element={<SupportLogin />} />
      <Route
        path="/reset-password"
        element={
          <>
            <NotificationStack
              stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
            />
            <ResetPassword />
          </>
        }
      />

      <Route
        path="/classrooms"
        element={
          <ProtectedRoute allowWhenTrialLocked>
            <Content>
              <RoleBasedView
                studentView={<StudentClassroomsView />}
                teacherView={<TeacherClassroomsView />}
              />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/lessons"
        element={
          <ProtectedRoute requiredRole="TEACHER" allowWhenTrialLocked>
            <Content>
              <TeacherLessonsView />
            </Content>
          </ProtectedRoute>
        }
      />

      {lessonSequencingEnabled && (
        <Route
          path="/lesson-sequencing"
          element={
            <ProtectedRoute requiredRole="TEACHER" allowWhenTrialLocked>
              <Content>
                <LessonSequencingPage />
              </Content>
            </ProtectedRoute>
          }
        />
      )}

      <Route
        path="/curriculum"
        element={
          <ProtectedRoute requiredRole="TEACHER" allowWhenTrialLocked>
            <Content>
              <CurriculumManagementPage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/curriculum/:curriculumId"
        element={
          <ProtectedRoute requiredRole="TEACHER" allowWhenTrialLocked>
            <Content>
              <CurriculumManagementPage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/classroom/:classroomId"
        element={
          <ProtectedRoute requiredRole="STUDENT">
            <Content>
              <StudentLessonsView />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route path="/slide-viewer" element={<SlideViewerPage />} />

      <Route
        path="/slide-viewer/*"
        element={
          <ProtectedRoute>
            <HtmlViewer />
          </ProtectedRoute>
        }
      />

      <Route
        path="/slide-editor"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <RuntimeProvider>
              <SlideEditor />
            </RuntimeProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vo-demo/:lessonId/:voiceModel?/:model?"
        element={
          <ProtectedRoute>
            {/* Next lesson navigates here in place; each lesson still gets a
                fresh player and providers, as when opened from the list. */}
            <RemountOnLessonChange>
              <NotificationStack
                stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
              />
              <div className="fullscreen">
                <RuntimeProvider>
                  <CooldownProvider>
                    <VoDemoContainer />
                  </CooldownProvider>
                </RuntimeProvider>
              </div>
            </RemountOnLessonChange>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vo-demo/preview/:lessonId/:voiceModel?/:model?"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <NotificationStack
              stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
            />
            <div className="fullscreen">
              <RuntimeProvider>
                <CooldownProvider>
                  <VoDemoContainer mode={TutorMode.PREVIEW} />
                </CooldownProvider>
              </RuntimeProvider>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spaces"
        element={
          <ProtectedRoute>
            <Content>
              <SpacesListPage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/spaces/create"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <Content>
              <CreateSpacePage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/spaces/:spaceId/recap"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <Content>
              <TeacherRecapPage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/spaces/:spaceId/snapshot/:userId"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <TeacherViewWorkPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/spaces/:spaceId/student-recap"
        element={
          <ProtectedRoute requiredRole="STUDENT">
            <Content>
              <StudentRecapPage />
            </Content>
          </ProtectedRoute>
        }
      />

      <Route
        path="/spaces/:spaceId"
        element={
          <ProtectedRoute>
            <NotificationStack
              stack={NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER]}
            />
            <FormProvider>
              <RoleBasedView
                teacherView={<TeacherSpacePage />}
                studentView={<StudentSpacePage />}
              />
            </FormProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute requiredRole="TEACHER">
            <DashboardView />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
